/** Browser-only canvas art for “I fueled Sam” cards. Do not call this on Render. */

const CHROME_COLOR = {
  amber: "#f59e0b",
  ruby: "#e11d48",
  emerald: "#10b981",
  standard: "#35e0f0",
};

/** Public path to the circular mission-patch emblem (transparent PNG). */
export const EMBLEM_SRC = "/mission-patch.png";

// Cache the emblem Image so repeated draws don't refetch. `undefined` = not
// attempted, `null` = failed (draw without emblem), Image = ready.
let emblemCache;
const emblemListeners = new Set();

function loadEmblem() {
  if (emblemCache !== undefined) return emblemCache;
  if (typeof Image === "undefined") { emblemCache = null; return null; }
  emblemCache = null; // mark attempted
  const img = new Image();
  img.onload = () => { emblemCache = img; emblemListeners.forEach((fn) => fn(img)); };
  img.onerror = () => { emblemCache = null; emblemListeners.forEach((fn) => fn(null)); };
  img.src = EMBLEM_SRC;
  return null;
}

/** Subscribe to emblem load; returns unsubscribe. Used so the card re-draws. */
export function onEmblemReady(fn) {
  emblemListeners.add(fn);
  return () => emblemListeners.delete(fn);
}

/**
 * Draw the card. Layout matches the Meet Sam fullscreen overlay frame:
 * charcoal panel, thin orange border, rivet corners, mono orange label
 * top-left, status line bottom. Patch emblem is a dimmed background watermark.
 * Pass an explicit `emblem` (Image | null) to control rendering (tests).
 */
export function drawShareCard(canvas, { rank, domain, amount, chrome = "standard", emblem, closerPct = null } = {}) {
  if (!canvas) return canvas;
  const ctx = canvas.getContext && canvas.getContext("2d");
  if (!ctx) return canvas;
  const w = canvas.width || 1200;
  const h = canvas.height || 630;
  const accent = CHROME_COLOR[chrome] || CHROME_COLOR.standard;
  const ORANGE = "#ff8c46";

  // Backdrop
  ctx.fillStyle = "#060a13";
  ctx.fillRect(0, 0, w, h);

  // Charcoal panel with thin orange border (overlay frame)
  const m = 26;
  ctx.fillStyle = "#141418";
  ctx.fillRect(m, m, w - m * 2, h - m * 2);
  ctx.strokeStyle = ORANGE;
  ctx.lineWidth = 3;
  ctx.strokeRect(m, m, w - m * 2, h - m * 2);

  // Rivet dots at the panel corners
  ctx.fillStyle = "#3a3a42";
  const rivet = (x, y) => { ctx.beginPath(); ctx.arc(x, y, 5, 0, Math.PI * 2); ctx.fill(); };
  const ri = m + 14;
  rivet(ri, ri); rivet(w - ri, ri); rivet(ri, h - ri); rivet(w - ri, h - ri);

  // Mono label, top-left
  ctx.textAlign = "left";
  ctx.textBaseline = "middle";
  ctx.fillStyle = ORANGE;
  ctx.font = "bold 26px monospace";
  ctx.fillText("I FUELED SAM // GETMEHOME.LOL", m + 34, m + 34);

  // Patch emblem watermark (dimmed, centered behind text)
  const img = emblem !== undefined ? emblem : (emblemCache !== undefined ? emblemCache : loadEmblem());
  if (img && img.width && img.height) {
    const eh = Math.round(h * 0.72);
    const ew = Math.round((eh * img.width) / img.height);
    ctx.save();
    ctx.globalAlpha = 0.14;
    ctx.drawImage(img, Math.round(w / 2 - ew / 2), Math.round(h * 0.5 - eh / 2), ew, eh);
    ctx.restore();
  }

  // Center stack
  ctx.textAlign = "center";
  ctx.fillStyle = ORANGE;
  ctx.font = "bold 84px sans-serif";
  ctx.fillText("I fueled Sam", w / 2, h * 0.34);

  ctx.fillStyle = "#dbe6f5";
  ctx.font = "38px sans-serif";
  ctx.fillText(domain || "GetMeHome.lol", w / 2, h * 0.5);

  if (rank) {
    ctx.fillStyle = accent;
    ctx.font = "bold 56px sans-serif";
    ctx.fillText(`#${rank}`, w / 2, h * 0.63);
  }

  if (amount != null && amount !== "") {
    ctx.fillStyle = ORANGE;
    ctx.font = "bold 42px sans-serif";
    ctx.fillText(`$${Number(amount).toLocaleString()}`, w / 2, h * 0.76);
  }

  // % closer to Earth (P2.4-live-feed: share card)
  if (closerPct != null && Number.isFinite(closerPct)) {
    const pctText = closerPct < 1 ? `${closerPct.toFixed(2)}%` : `${closerPct.toFixed(1)}%`;
    ctx.fillStyle = "#35e0f0";
    ctx.font = "bold 32px sans-serif";
    ctx.fillText(`${pctText} closer to Earth`, w / 2, h * 0.85);
  }

  // Bottom status bar (divider + labels), matching the overlay footer
  const dy = h - m - 52;
  ctx.strokeStyle = "#2a2a30";
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(m + 24, dy);
  ctx.lineTo(w - m - 24, dy);
  ctx.stroke();

  ctx.textAlign = "left";
  ctx.fillStyle = "#64748b";
  ctx.font = "22px monospace";
  ctx.fillText("SECTOR 7 · DEEP SPACE UPLINK", m + 34, dy + 30);

  ctx.textAlign = "right";
  ctx.fillStyle = ORANGE;
  ctx.fillText("GETMEHOME.LOL", w - m - 34, dy + 30);
  return canvas;
}
